// The shared, pure camera-gesture math (camera-pan-zoom; design.md D1): zoom-about-a-
// screen-point, pan-by-screen-delta, a wheel-delta zoom-factor mapping, and the one
// consolidated PROTOCOL §4 ±10% hysteresis function (replacing the three copy-pasted
// `defaultTierForZoom`s in `SessionController.tsx` and `Match.tsx`, tasks 1.1/1.2 — both
// were confirmed byte-for-byte identical before consolidating). No DOM, no React — the
// thin adapter half lives in `use-canvas-camera-gestures.ts`.
//
// Reads (never edits) `client/src/canvas/projection.ts`'s `Camera`/`Y_SQUASH`/
// `screenToWorld` — that surface is `pixel-art-pass`'s.

import { screenToWorld, Y_SQUASH, type Camera } from '../../canvas/projection';
import { Tier } from '../../net/wire/messages';

export interface CameraGestureBounds {
  minScale: number;
  maxScale: number;
}

export interface ScreenPoint {
  x: number;
  y: number;
}

/**
 * Zoom about a screen-space point (standard map convention): the world point currently
 * under `screenPoint` stays under `screenPoint` after the zoom. Derived by requiring
 * `worldToScreen(result, screenToWorld(camera, screenPoint)) === screenPoint` at the new
 * (bounds-clamped) scale — the same inversion `hiveCloseCamera` uses for its own centred
 * special case, generalized to an arbitrary screen point and an arbitrary new scale.
 */
export function zoomAboutScreenPoint(
  camera: Camera,
  screenPoint: ScreenPoint,
  factor: number,
  bounds: CameraGestureBounds,
): Camera {
  const newScale = Math.min(bounds.maxScale, Math.max(bounds.minScale, camera.scale * factor));
  if (newScale === camera.scale) {
    return camera;
  }
  const [worldX, worldY] = screenToWorld(camera, screenPoint);
  return {
    offsetX: worldX - screenPoint.x / newScale,
    offsetY: worldY - screenPoint.y / (newScale * Y_SQUASH),
    scale: newScale,
  };
}

/**
 * Pan by a screen-space pixel delta (drag). World-units-per-screen-pixel is `1/scale` on
 * the x-axis (since `Camera.scale` IS screen-units-per-world-unit by definition) and
 * `1/(scale*Y_SQUASH)` on the y-axis (the world's y-axis is visually squashed, so the same
 * screen-pixel drag corresponds to a larger world-y distance) — unlike the old,
 * un-squashed `Match.tsx` `panByPixels` this replaces (design.md D2/plan.md D7, a latent
 * anisotropy bug fixed as a side effect).
 */
export function panByScreenDelta(camera: Camera, dxPx: number, dyPx: number): Camera {
  if (dxPx === 0 && dyPx === 0) {
    return camera;
  }
  return {
    offsetX: camera.offsetX - dxPx / camera.scale,
    offsetY: camera.offsetY - dyPx / (camera.scale * Y_SQUASH),
    scale: camera.scale,
  };
}

/** The per-event zoom-in ceiling `wheelZoomFactor` approaches asymptotically as
 * `|deltaY|` grows, so one wheel notch (or one synthetic trackpad-pinch tick, however
 * large) never jumps zoom implausibly far in a single event. */
const WHEEL_FACTOR_CEILING = 1.6;
/** How quickly the factor approaches the ceiling as `|deltaY|` grows — tuned only enough
 * to be monotonic and bounded; exact "feel" is a Stage-4 finesse question (plan.md D3),
 * not this change's concern. Calibrated against PIXEL-mode deltas, which is what
 * {@link normalizeToPixels} converts every event into first. */
const WHEEL_SENSITIVITY = 0.006;

// --- deltaMode normalisation -------------------------------------------------------------
//
// `deltaY` alone does not describe a wheel event: it is a magnitude in whichever unit
// `deltaMode` names, and the SAME physical notch reports wildly different numbers in each.
// Feeding it to the curve raw — which this function did until 2026-08-07 — gave a line-mode
// mouse roughly a THIRTIETH of the zoom step a pixel-mode trackpad got: +1.1% against +32.2%,
// or about thirty notches to do one notch's work. That is indistinguishable from a wheel that
// does not work, and it was reported as exactly that.
//
// It reproduced on Windows and not in tests because the two populations differ: Windows mice
// and drivers commonly report LINE mode, Chrome trackpads report PIXEL, and every wheel test in
// this codebase constructed a `WheelEvent` without a `deltaMode` — which defaults to PIXEL. The
// mode was not mishandled, it was never read.

/** What one detent of a physical wheel reports in each unit. These are empirical
 * device/browser facts, not preferences: a notch is ~100 px in pixel mode (Chrome; 120 on
 * legacy Windows paths), 3 lines in line mode (the common Firefox/Windows report), and 1 page
 * in page mode. The per-unit multipliers below are DERIVED from them, so the calibration is
 * stated once as "what a notch is" rather than as three magic scale factors. */
export const PIXELS_PER_NOTCH = 100;
/** @see PIXELS_PER_NOTCH */
export const LINES_PER_NOTCH = 3;
/** @see PIXELS_PER_NOTCH */
export const PAGES_PER_NOTCH = 1;

/** Pixel-equivalent of one reported line/page. Derived so that one notch in ANY mode lands on
 * the same point of the response curve — the acceptance criterion is notch-equivalence, so
 * these are calibration constants, deliberately NOT claims about a real line height or
 * viewport size (a pure function has no viewport to measure, and plumbing one in to serve the
 * rarest of the three modes would buy nothing). */
const PIXELS_PER_LINE = PIXELS_PER_NOTCH / LINES_PER_NOTCH;
const PIXELS_PER_PAGE = PIXELS_PER_NOTCH / PAGES_PER_NOTCH;

/** `WheelEvent.DOM_DELTA_LINE` / `DOM_DELTA_PAGE`. Named here rather than read off
 * `WheelEvent` so this module keeps its no-DOM property. */
const DOM_DELTA_LINE = 1;
const DOM_DELTA_PAGE = 2;

/** The wheel event's three load-bearing fields, structurally — never the `WheelEvent` itself,
 * so this module stays DOM-free and a test can exercise it with a plain object literal. */
export interface WheelZoomInput {
  readonly deltaY: number;
  /** `WheelEvent.deltaMode`: the UNIT `deltaY` is expressed in. An unrecognised value is read
   * as pixels — a future or malformed mode must degrade to the commonest unit rather than to a
   * dead wheel. */
  readonly deltaMode: number;
  readonly ctrlKey: boolean;
}

/** Signed pixel-equivalent of a reported delta. Scaling the SIGNED value (not the magnitude)
 * is what keeps zoom-out zooming out in the coarse modes. */
function normalizeToPixels(deltaY: number, deltaMode: number): number {
  if (deltaMode === DOM_DELTA_LINE) {
    return deltaY * PIXELS_PER_LINE;
  }
  if (deltaMode === DOM_DELTA_PAGE) {
    return deltaY * PIXELS_PER_PAGE;
  }
  return deltaY;
}

/**
 * Maps a wheel event to a multiplicative zoom factor. Regular wheel and ctrl-wheel (the
 * browser's trackpad-pinch synthesis) are treated identically (plan.md D3, design.md
 * Non-Goals) — both zoom in for negative `deltaY`, out for positive, with a continuous,
 * monotonic, bounded response. `ctrlKey` is accepted (not ignored) so the call site's intent is
 * explicit and a future differentiated tuning has an obvious seam, but this change does not
 * branch on it.
 *
 * `deltaMode` is normalised to pixels FIRST, so one notch produces one comparable step whatever
 * unit the device reports it in — see the block comment above for the defect that requires it.
 */
export function wheelZoomFactor({ deltaY, deltaMode }: WheelZoomInput): number {
  const pixels = normalizeToPixels(deltaY, deltaMode);
  const magnitude = WHEEL_FACTOR_CEILING - 1;
  // 1 + magnitude * tanh(sensitivity * |pixels|), signed by -pixels: continuous, odd
  // around 0 (a zero delta -> factor 1), monotonic in |pixels|, and bounded in
  // (1/WHEEL_FACTOR_CEILING, WHEEL_FACTOR_CEILING) for any finite delta. A non-finite delta
  // falls through both comparisons to 1, so a garbage event cannot move the camera.
  const shaped = Math.tanh(WHEEL_SENSITIVITY * Math.abs(pixels));
  const factor = 1 + magnitude * shaped;
  return pixels < 0 ? factor : pixels > 0 ? 1 / factor : 1;
}

// --- two-finger pinch (WAG-93) -----------------------------------------------------------
//
// A pinch needs no response curve. A wheel notch is a device tick with no physical scale, so
// `wheelZoomFactor` has to *invent* one; two fingers state the scaling directly — the world
// should end up as many times bigger as the fingers ended up further apart. So the mapping is
// the plain ratio of separations, and the only judgement here is the degenerate case.

/** Below this finger separation (screen px) a pinch reports a factor of exactly 1. Two
 * fingers this close are a single blob as far as the ratio is concerned, and dividing by a few
 * pixels of touch noise turns a stationary pinch into an unbounded zoom. 8 px matches
 * `TAP_SLOP_PX` — the same "this much movement is noise" scale the tap classifier uses. */
export const MIN_PINCH_SEPARATION_PX = 8;

/** Euclidean screen distance between two fingers. */
export function pinchSeparation(a: ScreenPoint, b: ScreenPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** The screen point a pinch zooms about: the midpoint of the two fingers, which is the point
 * a player is intuitively holding still. Fed to {@link zoomAboutScreenPoint}, so the world
 * under the middle of the gesture stays under the middle of the gesture. */
export function pinchAnchor(a: ScreenPoint, b: ScreenPoint): ScreenPoint {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * Multiplicative zoom factor for a pinch whose finger separation went from `previous` to
 * `next`. Exactly 1 (no movement) for a non-finite or too-small separation on either side —
 * guarded up front rather than clamped afterwards, because the pathological input is a
 * *divisor*, not an out-of-range result.
 */
export function pinchZoomFactor(previous: number, next: number): number {
  if (
    !Number.isFinite(previous) ||
    !Number.isFinite(next) ||
    previous < MIN_PINCH_SEPARATION_PX ||
    next < MIN_PINCH_SEPARATION_PX
  ) {
    return 1;
  }
  return next / previous;
}

/**
 * The RENDER-facing `Camera` for a controlled `{centre, zoom}` view over a MEASURED
 * container pixel size — generalizes `hiveCloseCamera`'s own inversion (an arbitrary
 * centre/zoom instead of a fixed hive anchor/`COLONY_CLOSE_SCALE`), the exact inverse of
 * `worldToScreen` at the container's screen centre. `zoom` IS the render `Camera.scale`
 * directly (screen-units-per-world-unit) — the same axis both `ESTABLISHING_CAMERA.scale`
 * (0.2, Match's establishing shot) and `COLONY_CLOSE_SCALE` (1.0, Colony's) already sit on.
 * Shared by both screens' controlled cameras (Match.tsx, Colony.tsx) so the derivation is
 * written once.
 */
export function cameraForZoomCentre(
  centre: readonly [number, number],
  zoom: number,
  container: { width: number; height: number },
): Camera {
  return {
    offsetX: centre[0] - container.width / 2 / zoom,
    offsetY: centre[1] - container.height / 2 / (zoom * Y_SQUASH),
    scale: zoom,
  };
}

/**
 * Live mode always requests the complete rounds-style detail frame. Zoom changes presentation
 * only: `canvas/symbols.ts` turns individual bees and flowers into dots when sprites stop being
 * legible. Keeping the data tier Close is what preserves one dot per entity at overview scale.
 */
export function defaultTierForZoom(_zoom: number, _current: Tier): Tier {
  return Tier.Close;
}
